import React, { useState, useRef, useCallback } from "react";
import moment from "moment";
import { commonService } from "../../../../../utils/commonService";

/**
 * InteractiveLineChart provides high-performance, responsive hover interactivity
 * for Yield Radar line charts (Booking curve, Plan mix pace, etc.).
 *
 * Features:
 * - Dynamic vertical crosshair tracking the cursor aligned to days-before-arrival.
 * - Glowing snap indicators (circles) on active curves (TY on the books, Forecast, STLY).
 * - Rich, floating glassmorphic tooltip card showing lead time, calendar date,
 *   occupancy %, room counts, and pace variance (lead/lag vs. STLY).
 * - Full touch and pointer support with boundary protection.
 */
export default function InteractiveLineChart({ config, propertyCode, disabled }) {
  const {
    series = [],
    yMax = 100,
    yLabel = "Occupancy %",
    marker = null,
    w = 620,
    h = 240,
    L = 44,
    R = 12,
    T = 12,
    B = 30,
    SHOW = 90,
    isOccupancy = true,
    capacity = 0,
    stayDate = null,
  } = config || {};

  const [hoverData, setHoverData] = useState(null);
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  // Coordinate projection functions (match yieldRadarModel.js exactly)
  const X = useCallback(
    (x) => L + ((SHOW - x) / SHOW) * (w - L - R),
    [L, R, SHOW, w]
  );
  const Y = useCallback(
    (v) => T + (1 - Math.max(0, Math.min(yMax, v)) / yMax) * (h - T - B),
    [B, T, h, yMax]
  );

  // Find value from series points at target x
  const findValueAt = useCallback((pts, targetX) => {
    if (!pts || !pts.length) return null;
    const exact = pts.find((p) => p[0] === targetX);
    if (exact) return exact[1];
    let closest = null;
    let minDist = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const dist = Math.abs(pts[i][0] - targetX);
      if (dist < minDist) {
        minDist = dist;
        closest = pts[i];
      }
    }
    return closest ? closest[1] : null;
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      if (disabled) return;
      if (!svgRef.current || !containerRef.current) return;

      const rect = svgRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const clientX =
        e.clientX !== undefined ? e.clientX : e.touches?.[0]?.clientX;
      const clientY =
        e.clientY !== undefined ? e.clientY : e.touches?.[0]?.clientY;

      if (clientX == null || clientY == null) return;

      const relX = clientX - rect.left;
      const relY = clientY - rect.top;

      // Scale from container/screen pixels to SVG viewBox space
      const svgX = (relX / rect.width) * w;
      const svgY = (relY / rect.height) * h;

      // Only activate when within reasonable chart plot bounds
      if (
        svgX < L - 10 ||
        svgX > w - R + 10 ||
        svgY < T - 15 ||
        svgY > h - B + 15
      ) {
        setHoverData(null);
        return;
      }

      // Compute exact days before arrival and clamp/round
      const exactDays = SHOW - ((svgX - L) / (w - L - R)) * SHOW;
      const snappedDays = Math.round(Math.max(0, Math.min(SHOW, exactDays)));

      // Categorize series
      const lySeries =
        series.find((s) => s.key === "ly" || s.label === "STLY") ||
        series[0] ||
        null;
      const tySeries =
        series.find(
          (s) =>
            s.key === "ty" ||
            s.label === "This year on the books" ||
            s.label === "This year"
        ) ||
        series[1] ||
        null;
      const fcSeries =
        series.find(
          (s) => s.key === "fc" || s.label === "Forecast to arrival"
        ) ||
        series[2] ||
        null;

      // Determine active TY curve (actual OTB vs forecast)
      const isPastMarker = marker != null && snappedDays < marker;
      const activeCurrentSeries =
        isPastMarker && fcSeries ? fcSeries : tySeries || fcSeries;

      const currentVal = activeCurrentSeries
        ? findValueAt(activeCurrentSeries.pts, snappedDays)
        : null;
      const stlyVal = lySeries ? findValueAt(lySeries.pts, snappedDays) : null;

      if (currentVal == null && stlyVal == null) {
        setHoverData(null);
        return;
      }

      // Compute room counts if applicable
      const tyRooms =
        currentVal != null
          ? isOccupancy && capacity > 0
            ? Math.round((currentVal / 100) * capacity)
            : Math.round(currentVal)
          : null;

      const stlyRooms =
        stlyVal != null
          ? isOccupancy && capacity > 0
            ? Math.round((stlyVal / 100) * capacity)
            : Math.round(stlyVal)
          : null;

      // Compute pace variance (lead/lag vs. STLY)
      let variance = null;
      if (currentVal != null && stlyVal != null) {
        const diffVal = currentVal - stlyVal;
        const diffRooms =
          tyRooms != null && stlyRooms != null ? tyRooms - stlyRooms : null;
        variance = {
          diffVal,
          diffRooms,
          status:
            diffVal > 0.05 ? "ahead" : diffVal < -0.05 ? "behind" : "even",
        };
      }

      // Compute calendar date corresponding to this lead time
      let leadDateStr = null;
      if (stayDate && moment(stayDate, "YYYY-MM-DD", true).isValid()) {
        leadDateStr = moment(stayDate, "YYYY-MM-DD")
          .subtract(snappedDays, "days")
          .format("ddd, MMM D, YYYY");
      }

      const mouseX = clientX - containerRect.left;
      const mouseY = clientY - containerRect.top;

      setHoverData({
        snappedDays,
        svgX: X(snappedDays),
        mouseX,
        mouseY,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        isPastMarker,
        isMarker: marker != null && snappedDays === marker,
        leadDateStr,
        values: {
          ty: {
            val: currentVal,
            rooms: tyRooms,
            label: isPastMarker
              ? "Forecast to arrival"
              : activeCurrentSeries?.label || "This year on the books",
            color: activeCurrentSeries?.color || "var(--gold)",
            isForecast: isPastMarker,
          },
          stly: {
            val: stlyVal,
            rooms: stlyRooms,
            label: lySeries?.label || "STLY",
            color: lySeries?.color || "var(--muted)",
          },
          variance,
        },
      });
    },
    [
      disabled,
      w,
      h,
      L,
      R,
      T,
      B,
      SHOW,
      series,
      marker,
      findValueAt,
      isOccupancy,
      capacity,
      stayDate,
      X,
    ]
  );

  const handlePointerLeave = useCallback(() => {
    setHoverData(null);
  }, []);

  // Y-axis tick values
  const yTicks = [0, 1, 2, 3, 4].map((k) => (yMax * k) / 4);
  const xTicks =
    SHOW > 90 ? [SHOW, 90, 60, 30, 14, 7, 0] : [90, 60, 30, 14, 7, 0];

  // Tooltip positioning calculations
  let tooltipStyle = null;
  if (hoverData) {
    const tooltipWidth = 230;
    const tooltipHeight = 150;
    const { mouseX, mouseY, containerWidth, containerHeight } = hoverData;

    // Flip to left if too close to right edge
    let left = mouseX + 16;
    if (mouseX + tooltipWidth + 24 > containerWidth) {
      left = Math.max(8, mouseX - tooltipWidth - 16);
    }

    // Clamp vertically within container
    let top = mouseY - 45;
    if (top < 8) top = 8;
    if (top + tooltipHeight > containerHeight - 8) {
      top = Math.max(8, containerHeight - tooltipHeight - 8);
    }

    tooltipStyle = {
      left: `${left}px`,
      top: `${top}px`,
    };
  }

  return (
    <div
      className="interactive-chart-wrapper"
      ref={containerRef}
      onMouseMove={handlePointerMove}
      onMouseLeave={handlePointerLeave}
      onTouchStart={handlePointerMove}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerLeave}
      id={commonService.generateGlobalClickTrackerID(
        "YieldRadar",
        "interactive-booking-curve",
        propertyCode
      )}
    >
      <svg
        ref={svgRef}
        className="chart"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`${yLabel} by days before arrival`}
      >
        {/* Plot area background frame */}
        <rect
          x={L}
          y={T}
          width={w - L - R}
          height={h - T - B}
          fill="none"
          stroke="var(--line)"
        />

        {/* Horizontal grid lines & Y labels */}
        {yTicks.map((v) => (
          <g key={`ytick-${v}`}>
            <line
              x1={L}
              x2={w - R}
              y1={Y(v)}
              y2={Y(v)}
              stroke="var(--line)"
              opacity=".6"
            />
            <text
              x={L - 6}
              y={Y(v) + 3}
              textAnchor="end"
              fontSize="10"
              fill="var(--muted)"
              fontFamily="JetBrains Mono, monospace"
            >
              {isOccupancy ? `${Math.round(v)}%` : Math.round(v)}
            </text>
          </g>
        ))}

        {/* X axis ticks */}
        {xTicks.map((x) => (
          <text
            key={`xtick-${x}`}
            x={X(x)}
            y={h - B + 14}
            textAnchor="middle"
            fontSize="10"
            fill="var(--muted)"
            fontFamily="JetBrains Mono, monospace"
          >
            {x}
          </text>
        ))}

        {/* Axis Titles */}
        <text
          x={(L + w - R) / 2}
          y={h - 3}
          textAnchor="middle"
          fontSize="10.5"
          fill="var(--muted)"
        >
          Days before arrival
        </text>
        <text
          transform={`translate(11 ${(T + h - B) / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize="10.5"
          fill="var(--muted)"
        >
          {yLabel}
        </text>

        {/* "Today" marker line */}
        {marker != null && (
          <g className="chart-marker-today">
            <line
              x1={X(marker)}
              x2={X(marker)}
              y1={T}
              y2={h - B}
              stroke="var(--text)"
              strokeDasharray="2 3"
              opacity=".5"
            />
            <text
              x={X(marker) + 4}
              y={T + 11}
              fontSize="10.5"
              fill="var(--text)"
            >
              Today
            </text>
          </g>
        )}

        {/* Line paths for each series */}
        {series.map((sr, idx) => {
          if (!sr.pts || !sr.pts.length) return null;
          const d = sr.pts
            .map(
              (p, k) =>
                `${k ? "L" : "M"}${X(p[0]).toFixed(1)},${Y(
                  Math.min(yMax, p[1])
                ).toFixed(1)}`
            )
            .join("");
          return (
            <path
              key={`series-${idx}-${sr.label || sr.key || idx}`}
              d={d}
              fill="none"
              stroke={sr.color}
              strokeWidth={sr.width || 2.2}
              strokeDasharray={sr.dash || undefined}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}

        {/* Interactive hover overlay and active indicators */}
        <rect
          x={L}
          y={T}
          width={w - L - R}
          height={h - T - B}
          fill="transparent"
          style={{ cursor: disabled ? "default" : "crosshair" }}
        />

        {hoverData && (
          <g className="chart-hover-elements" pointerEvents="none">
            {/* Vertical crosshair line */}
            <line
              className="chart-crosshair"
              x1={hoverData.svgX}
              x2={hoverData.svgX}
              y1={T}
              y2={h - B}
            />

            {/* STLY curve dot */}
            {hoverData.values.stly.val != null && (
              <g className="chart-dot-stly">
                <circle
                  className="chart-dot-outer"
                  cx={hoverData.svgX}
                  cy={Y(hoverData.values.stly.val)}
                  r="7"
                  fill={hoverData.values.stly.color}
                  opacity="0.25"
                />
                <circle
                  className="chart-dot-inner"
                  cx={hoverData.svgX}
                  cy={Y(hoverData.values.stly.val)}
                  r="4"
                  fill={hoverData.values.stly.color}
                />
              </g>
            )}

            {/* TY / Forecast curve dot */}
            {hoverData.values.ty.val != null && (
              <g className="chart-dot-ty">
                <circle
                  className="chart-dot-outer"
                  cx={hoverData.svgX}
                  cy={Y(hoverData.values.ty.val)}
                  r="7"
                  fill={hoverData.values.ty.color}
                  opacity="0.25"
                />
                <circle
                  className="chart-dot-inner"
                  cx={hoverData.svgX}
                  cy={Y(hoverData.values.ty.val)}
                  r="4"
                  fill={hoverData.values.ty.color}
                />
              </g>
            )}
          </g>
        )}
      </svg>

      {/* Floating Glassmorphic Tooltip Card */}
      {hoverData && tooltipStyle && (
        <div className="chart-tooltip" style={tooltipStyle}>
          <div className="chart-tooltip-header">
            <div className="chart-tooltip-title-wrap">
              <span className="chart-tooltip-title">
                {hoverData.snappedDays === 0
                  ? "Arrival Day"
                  : hoverData.isMarker
                  ? `Today (${hoverData.snappedDays}d out)`
                  : `${hoverData.snappedDays} days out`}
              </span>
              {hoverData.isMarker && (
                <span className="chart-tooltip-badge today">Today</span>
              )}
              {hoverData.isPastMarker && (
                <span className="chart-tooltip-badge forecast">Forecast</span>
              )}
            </div>
            {hoverData.leadDateStr && (
              <div className="chart-tooltip-date">{hoverData.leadDateStr}</div>
            )}
          </div>

          <div className="chart-tooltip-body">
            {/* This Year (OTB or Forecast) */}
            {hoverData.values.ty.val != null && (
              <div className="chart-tooltip-row">
                <span className="chart-tooltip-label">
                  <span
                    className={`chart-tooltip-indicator ${
                      hoverData.values.ty.isForecast ? "dash" : ""
                    }`}
                    style={{
                      background: hoverData.values.ty.isForecast
                        ? undefined
                        : hoverData.values.ty.color,
                    }}
                  />
                  <span>
                    {hoverData.values.ty.isForecast ? "Forecast" : "This year"}
                  </span>
                </span>
                <div className="chart-tooltip-val-wrap">
                  <span className="chart-tooltip-val">
                    {isOccupancy
                      ? `${hoverData.values.ty.val.toFixed(1)}%`
                      : `${commonService.formateRoom(
                          Math.round(hoverData.values.ty.val)
                        )}`}
                  </span>
                  {isOccupancy && hoverData.values.ty.rooms != null && (
                    <span className="chart-tooltip-sub">
                      ({commonService.formateRoom(hoverData.values.ty.rooms)}{" "}
                      rms)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* STLY */}
            {hoverData.values.stly.val != null && (
              <div className="chart-tooltip-row">
                <span className="chart-tooltip-label">
                  <span
                    className="chart-tooltip-indicator"
                    style={{ background: hoverData.values.stly.color }}
                  />
                  <span>STLY</span>
                </span>
                <div className="chart-tooltip-val-wrap">
                  <span className="chart-tooltip-val">
                    {isOccupancy
                      ? `${hoverData.values.stly.val.toFixed(1)}%`
                      : `${commonService.formateRoom(
                          Math.round(hoverData.values.stly.val)
                        )}`}
                  </span>
                  {isOccupancy && hoverData.values.stly.rooms != null && (
                    <span className="chart-tooltip-sub">
                      ({commonService.formateRoom(hoverData.values.stly.rooms)}{" "}
                      rms)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Pace Variance Pill */}
            {hoverData.values.variance && (
              <div
                className={`chart-tooltip-variance ${hoverData.values.variance.status}`}
              >
                {hoverData.values.variance.status === "ahead" ? (
                  <>
                    <span>▲</span>
                    <span>
                      {isOccupancy
                        ? `+${hoverData.values.variance.diffVal.toFixed(
                            1
                          )}% ahead`
                        : `+${commonService.formateRoom(
                            Math.round(hoverData.values.variance.diffVal)
                          )} rms ahead`}
                      {isOccupancy &&
                        hoverData.values.variance.diffRooms != null &&
                        ` (+${commonService.formateRoom(
                          hoverData.values.variance.diffRooms
                        )} rms)`}
                    </span>
                  </>
                ) : hoverData.values.variance.status === "behind" ? (
                  <>
                    <span>▼</span>
                    <span>
                      {isOccupancy
                        ? `${hoverData.values.variance.diffVal.toFixed(
                            1
                          )}% behind`
                        : `${commonService.formateRoom(
                            Math.round(hoverData.values.variance.diffVal)
                          )} rms behind`}
                      {isOccupancy &&
                        hoverData.values.variance.diffRooms != null &&
                        ` (${commonService.formateRoom(
                          hoverData.values.variance.diffRooms
                        )} rms)`}
                    </span>
                  </>
                ) : (
                  <span>On pace with STLY</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
